import { useC2pa } from "@contentauth/react";
import { Box, Link, Typography } from "@mui/material";
import { L2ManifestStore, createL2ManifestStore, generateVerifyUrl } from "c2pa";
import { ManifestSummary } from "c2pa-wc/dist/components/ManifestSummary";
import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Divider } from "rsuite";
import { C2PAIcon } from "../../../assets/images/icons/C2PAIcon";
import { C2PAIconTextToImage } from "../../../assets/images/icons/C2PAIconTextToImage";
import Calendar from "../../../assets/images/icons/Calendar";
import Certificate from "../../../assets/images/icons/Certificate";
import CloseIcon from "../../../assets/images/icons/CloseIcon";
import { isTGApp, isTextToImageApp } from "../../../utils";
import BriaButton from "../BriaButton/BriaButton";
import BriaIconButton from "../BriaIconButton/BriaIconButton";
import BriaTooltip from "../BriaTooltip/BriaTooltip";
import styles from "./C2PAButton.module.scss";

interface IProps {
	imageUrl: string;
	className?: string;
	isHovered?: boolean;
	btnClassName?: string;
}

function C2PAButton({ imageUrl, className, isHovered, btnClassName }: IProps) {
	const { t } = useTranslation("translation", { keyPrefix: "c2paPupup" });
	const provenance = useC2pa(imageUrl);
	const viewMoreUrl = generateVerifyUrl(imageUrl);
	const [isOpen, setIsOpen] = useState(false);
	const [date, setDate] = useState<string>();
	const [manifestStore, setManifestStore] = useState<L2ManifestStore | null>(null);
	const summaryRef = useRef<ManifestSummary>();
	const wrapperRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		let disposeFn = () => {};
		if (!provenance?.manifestStore?.activeManifest) return;

		try {
			createL2ManifestStore(provenance.manifestStore)
				.then(({ manifestStore, dispose }) => {
					try {
						setManifestStore(manifestStore);
						const isoDateString = manifestStore?.signature?.isoDateString;
						if (isoDateString) {
							const date = new Date(isoDateString);
							setDate(
								date.toLocaleDateString("en-US", {
									month: "short",
									day: "numeric",
									year: "numeric",
								}),
							);
						}
						disposeFn = dispose;
					} catch (error) {
						console.error("C2PA manifest processing error:", error);
					}
				})
				.catch((error) => {
					console.error("C2PA L2 manifest store creation error:", error);
				});
		} catch (error) {
			console.error("C2PA initialization error:", error);
		}

		return disposeFn;
	}, [provenance?.manifestStore?.activeManifest]);

	useEffect(() => {
		try {
			const summaryElement = summaryRef.current;
			if (summaryElement && manifestStore) {
				summaryElement.manifestStore = manifestStore;
				summaryElement.viewMoreUrl = viewMoreUrl;
			}
		} catch (error) {
			console.error("C2PA summary element setup error:", error);
		}
	}, [summaryRef, manifestStore]);

	useEffect(() => {
		try {
			const handleClickOutside = (event: MouseEvent) => {
				try {
					if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
						setIsOpen(false);
					}
				} catch (error) {
					console.debug("C2PA click outside handler error:", error);
				}
			};

			if (isOpen) {
				document.addEventListener("mousedown", handleClickOutside);
			}

			return () => {
				document.removeEventListener("mousedown", handleClickOutside);
			};
		} catch (error) {
			console.debug("C2PA click outside setup error:", error);
		}
	}, [isOpen]);

	useEffect(() => {
		!isHovered && setIsOpen(false);
	}, [isHovered]);

	if (!provenance?.manifestStore?.activeManifest) {
		return null;
	}

	try {
		return (
			<Box ref={wrapperRef} className={clsx(styles.c2paButton, className)}>
				<BriaTooltip title={t("tooltip")}>
					<BriaButton
						buttonType="tertiaryMedium"
						className={clsx(styles.c2paIcon, btnClassName, "iconHover", {
							[styles.customClass]: isTextToImageApp() && !isTGApp(),
						})}
						onClick={(e) => {
							e.stopPropagation();
							setIsOpen(!isOpen);
						}}
					>
						{isTextToImageApp() && !isTGApp() ? <C2PAIconTextToImage /> : <C2PAIcon />}
					</BriaButton>
				</BriaTooltip>
				{isOpen && (
					<Box className={styles.c2paContent}>
						<BriaIconButton className={styles.closeBtn} onClick={() => setIsOpen(false)}>
							<CloseIcon />
						</BriaIconButton>
						<Typography className={styles.title}>{t("title")}</Typography>
						<Typography className={styles.subTitle}>
							{t("description")}
							<Link onClick={() => window.open(viewMoreUrl, "_blank")} className={styles.link}>
								More details
							</Link>
						</Typography>
						<Divider className={styles.divider} />
						<Box className={styles.c2paManifest}>
							<Box className={styles.contentWrapper}>
								<Certificate className={styles.certificateIcon} />
								<Typography>
									{t("issuedBy")} {manifestStore?.signature?.issuer}
								</Typography>
							</Box>
							<Box className={styles.contentWrapper}>
								<Calendar className={styles.calendarIcon} />
								<Typography>{date && `${t("issuedDate")} ${date}`}</Typography>
							</Box>
						</Box>
					</Box>
				)}
			</Box>
		);
	} catch (error) {
		console.error("C2PA component rendering error:", error);
		return null;
	}
}

export default C2PAButton;
